import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTable, useGlobalFilter, useFilters, usePagination } from "react-table";
import { Row, Col, Button } from "react-bootstrap";
import { GlobalFilter } from "../components/table/FilteringTable/GlobalFilter";
import "../components/table/FilteringTable/filtering.css";
import { ColumnFilter } from "../components/table/FilteringTable/ColumnFilter";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useDispatch } from "react-redux";
import { Fn_DeleteData, Fn_FillListData } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";

import useSession from "../utils/useSession";

export const PageList_DepartmentMaster = () => {
  const [gridData, setGridData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { firmId } = useSession();

  const LIST_API_URL = `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Tbl.F_FirmMaster/${firmId || 0}`;
  const DELETE_API_URL = `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster`;

  const loadData = useCallback(() => {
    if (firmId) {
      Fn_FillListData(dispatch, setGridData, "gridData", LIST_API_URL).catch((err) => {
        console.error("Failed to load departments:", err);
      });
    } else {
      setGridData([]);
    }
  }, [dispatch, LIST_API_URL, firmId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const btnAddOnClick   = ()   => navigate("/AddDepartment", { state: { Id: 0 } });
  const btnMultiOnClick  = ()   => navigate("/MultiDepartmentEntry");
  const btnEditOnClick   = (Id) => navigate("/AddDepartment", { state: { Id } });

  const handleDelete = (id, name) => {
    if (!id) return;
    if (window.confirm(`Are you sure you want to delete this department master${name ? ` (${name})` : ""}?`)) {
      Fn_DeleteData(dispatch, setGridData, Number(id), DELETE_API_URL, LIST_API_URL)
        .then(() => {
          toast.success("Department deleted successfully");
          loadData();
        })
        .catch((error) => {
          console.error("Failed to delete department:", error);
        });
    }
  };

  const COLUMNS = [
    {
      Header: "Department Name",
      Footer: "Department Name",
      accessor: "Name",
      Filter: ColumnFilter,
    },
    {
      Header: "Not In Use",
      accessor: "IsNotInUse",
      Filter: ColumnFilter,
      Cell: ({ value }) => (value ? "Yes" : "No"),
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <Button variant="primary" size="sm" className="me-2" onClick={() => btnEditOnClick(row.original.Id)} title="Edit">
            <i className="fa fa-edit" />
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleDelete(row.original.Id, row.original.Name)} title="Delete">
            <i className="fa fa-trash" />
          </Button>
        </div>
      ),
    },
  ];

  const columns = useMemo(() => COLUMNS, []);
  const data = useMemo(() => gridData, [gridData]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    pageCount,
    pageOptions,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  return (
    <Container fluid className="page-content">
      <Row className="mb-3 align-items-center">
        <Col md="6">
          <h4 className="page-title mb-0" style={{ fontFamily: "Poppins" }}>Department Master</h4>
        </Col>
        <Col md="auto" className="ms-auto d-flex gap-2">
          <Button type="button" onClick={btnAddOnClick} variant="success" size="sm">
            <i className="fas fa-plus me-1"></i>Add New
          </Button>
          <Button type="button" onClick={btnMultiOnClick} variant="primary" size="sm" style={{ whiteSpace: "nowrap" }}>
            <i className="fas fa-table me-1"></i>Add Multi Department
          </Button>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md="12">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </Col>
      </Row>
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table {...getTableProps()} className="table dataTable display">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        {column.render("Header")}
                        {column.canFilter ? column.render("Filter") : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="d-flex justify-content-between">
              <span>
                Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
              </span>
              <span className="table-index">
                Go to page:{" "}
                <input
                  type="number"
                  className="ml-2"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                    gotoPage(pageNumber);
                  }}
                />
              </span>
            </div>
            <div className="text-center">
              <div className="filter-pagination mt-3">
                <button className="previous-button" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>
                <button className="previous-button" onClick={() => previousPage()} disabled={!canPreviousPage}>Previous</button>
                <button className="next-button" onClick={() => nextPage()} disabled={!canNextPage}>Next</button>
                <button className="next-button" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default PageList_DepartmentMaster;
